import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { UserCheck, Clock, Check, Image as ImageIcon, X, MessageSquare, Maximize2, Download, Plus } from "lucide-react";
import { apiExpertCredits, apiExpertSubmit, apiExpertList, http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import TicketModal from "./TicketModal";

const ASSETS = ["BTC", "ETH", "SOL", "XRP", "BNB", "DOGE", "ADA", "AVAX", "Custom"];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const ALLOWED_MIMES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

export default function ExpertReview() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [requestsLoaded, setRequestsLoaded] = useState(false);
  const [asset, setAsset] = useState("BTC");
  const [customAsset, setCustomAsset] = useState("");
  const [question, setQuestion] = useState("");
  const [image, setImage] = useState(null);          // { base64, mime, preview, name, sizeKB }
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState({ reviews_remaining: 0, extra_reviews: 0, monthly_allowance: 2 });
  const [requests, setRequests] = useState([]);
  const [ticketReview, setTicketReview] = useState(null);
  const [enlargedImg, setEnlargedImg] = useState(null);
  const fileInputRef = useRef(null);

  const loadReviews = async () => {
    try { setReviews(await apiExpertCredits()); } catch { /* ignore */ }
  };
  const loadRequests = async () => {
    try { setRequests(await apiExpertList() || []); } catch { /* ignore */ }
    setRequestsLoaded(true);
  };
  useEffect(() => { loadReviews(); loadRequests(); }, []);
  const showHistoryAnchor = hash === "#your-requests";
  useEffect(() => {
    if (showHistoryAnchor && requestsLoaded) {
      document.getElementById("your-requests")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showHistoryAnchor, requestsLoaded]);

  const finalAsset = asset === "Custom" ? customAsset.trim().toUpperCase() : asset;

  const onFile = (file) => {
    if (!file) return;
    if (!ALLOWED_MIMES.includes(file.type)) {
      toast.error("Image must be PNG, JPG, or WEBP"); return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be 2 MB or smaller"); return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target.result;
      const base64 = result.split(",")[1];
      setImage({
        base64,
        mime: file.type,
        preview: result,
        name: file.name,
        sizeKB: Math.round(file.size / 1024),
      });
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!finalAsset || finalAsset.length < 2) {
      toast.error("Please choose an asset (e.g. BTC, ETH)."); return;
    }
    setSubmitting(true);
    try {
      await apiExpertSubmit({
        asset: finalAsset,
        question: question.trim() || null,
        image_base64: image?.base64 || null,
        image_mime: image?.mime || null,
      });
      toast.success("Expert review request submitted.");
      setQuestion("");
      setImage(null);
      await loadReviews();
      await loadRequests();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const openImage = async (reviewId) => {
    try {
      const { data } = await http.get(`/expert-review/${reviewId}/image`);
      setEnlargedImg({
        src: `data:${data.image_mime};base64,${data.image_base64}`,
        filename: `chartlenz-chart-${reviewId.slice(0, 8)}.${(data.image_mime || "image/png").split("/")[1]}`,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openResponseImage = async (reviewId, img) => {
    try {
      const { data } = await http.get(`/expert-review/${reviewId}/response-images/${img.id}`);
      setEnlargedImg({
        src: `data:${data.image_mime};base64,${data.image_base64}`,
        filename: data.filename || `chartlenz-review-${reviewId.slice(0, 8)}-${img.id.slice(0, 6)}.${(data.image_mime || "image/png").split("/")[1]}`,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusStyle = (s) => {
    if (s === "completed") return { color: "#00FF66", icon: Check, label: "completed" };
    if (s === "in_progress") return { color: "#3FA7FF", icon: Clock, label: "in progress" };
    return { color: "#FFB000", icon: Clock, label: "pending" };
  };

  return (
    <motion.section
      className="pt-12 md:pt-16"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
    >
      <div className="max-w-3xl mx-auto text-center mb-8">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
          ── manual analysis ──
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white leading-[1.05]">
          Request an Expert Review
        </h1>
        <p className="mt-4 text-base text-neutral-400 max-w-2xl mx-auto leading-relaxed">
          A human trader analyses your asset on TradingView, across multiple
          timeframes, using full market context.
        </p>
      </div>

      {/* Submit form */}
      <div className="max-w-3xl mx-auto border border-neutral-900 bg-neutral-950 p-6 mb-6" data-testid="expert-review-form">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 pb-4 border-b border-neutral-900">
          <div className="flex items-center gap-2" data-testid="reviews-balance">
            <UserCheck className="w-4 h-4 text-[#00E5FF]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">reviews this cycle</span>
            <span className="font-mono text-lg text-white">
              {reviews.reviews_remaining ?? 0} / {reviews.monthly_allowance ?? 2}
            </span>
            {(reviews.extra_reviews ?? 0) > 0 && (
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#FFB000]">
                · +{reviews.extra_reviews} extra
              </span>
            )}
          </div>
          <button
            onClick={() => navigate("/buy-credits")}
            data-testid="buy-credits-btn"
            className="inline-flex items-center gap-1.5 border border-neutral-800 hover:border-[#FFB000] hover:text-[#FFB000] text-neutral-300 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 transition-colors"
          >
            <Plus className="w-3 h-3" /> buy extra reviews
          </button>
        </div>

        {/* Delivery SLA notice */}
        <div className="mb-5 flex items-start gap-2.5 border border-[#00E5FF]/30 bg-[#00E5FF]/[0.04] px-3 py-2.5" data-testid="sla-notice">
          <Clock className="w-4 h-4 text-[#00E5FF] mt-0.5 flex-shrink-0" />
          <div className="text-xs text-neutral-200 leading-relaxed">
            <span className="font-bold text-white">Delivered within 3 business days.</span>{" "}
            <span className="text-neutral-400">You'll receive an email as soon as your review is ready.</span>
          </div>
        </div>

        <Section label="Crypto asset">
          <div className="flex flex-wrap gap-2" data-testid="asset-picker">
            {ASSETS.map((a) => (
              <button
                key={a}
                onClick={() => setAsset(a)}
                data-testid={`asset-${a.toLowerCase()}`}
                className={`font-mono text-xs uppercase tracking-[0.15em] px-3 py-2 border ${
                  asset === a
                    ? "border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/[0.06]"
                    : "border-neutral-800 text-neutral-300 hover:border-neutral-600"
                }`}
              >{a}</button>
            ))}
          </div>
          {asset === "Custom" && (
            <input
              value={customAsset}
              onChange={(e) => setCustomAsset(e.target.value)}
              placeholder="e.g. LINK, MATIC, ATOM"
              data-testid="custom-asset-input"
              className="mt-3 w-full bg-black border border-neutral-800 px-3 py-2 font-mono text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-[#00E5FF]"
            />
          )}
        </Section>

        <Section label="Optional question or context">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={4}
            placeholder="e.g. Long-term outlook? Best swing entry zones?"
            data-testid="question-input"
            className="w-full bg-black border border-neutral-800 px-3 py-2 font-mono text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-[#00E5FF] resize-none"
          />
        </Section>

        <Section label="Optional chart screenshot (max 1 · PNG/JPG/WEBP · 2 MB)">
          {!image ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="image-pick-btn"
              className="w-full border border-dashed border-neutral-800 hover:border-neutral-600 bg-black/40 py-6 flex flex-col items-center justify-center gap-1.5 transition-colors text-neutral-500 hover:text-neutral-300"
            >
              <ImageIcon className="w-5 h-5" />
              <span className="text-xs font-mono uppercase tracking-[0.25em]">attach screenshot</span>
              <span className="text-[10px] font-mono text-neutral-700 normal-case">optional · for reference only</span>
            </button>
          ) : (
            <div className="border border-neutral-800 bg-black p-3 flex items-center gap-3" data-testid="image-preview">
              <img src={image.preview} alt="chart preview"
                   className="w-24 h-16 object-cover border border-neutral-800" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white truncate">{image.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                  {image.sizeKB} kb · {image.mime.replace("image/", "")}
                </div>
              </div>
              <button
                onClick={() => setImage(null)}
                data-testid="image-remove"
                className="text-neutral-500 hover:text-white p-2"
                aria-label="remove image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            className="hidden"
            data-testid="image-input"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </Section>

        <button
          onClick={submit}
          disabled={submitting}
          data-testid="submit-expert-review-button"
          className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <UserCheck className="w-4 h-4" />
          {submitting ? "Submitting..." : "Submit · uses 1 Expert Review"}
        </button>
      </div>

      {/* Request history */}
      {(requests.length > 0 || showHistoryAnchor) && (
        <div id="your-requests" className="max-w-3xl mx-auto scroll-mt-24" data-testid="request-history">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-3">
            Your requests
          </div>
          {requestsLoaded && requests.length === 0 && (
            <div className="text-xs text-neutral-500" data-testid="request-history-empty">No Expert Review requests yet.</div>
          )}
          <div className="space-y-2">
            {requests.map((r) => {
              const st = statusStyle(r.status);
              const Icon = st.icon;
              return (
                <div key={r.id} className="border border-neutral-900 bg-neutral-950 p-4" data-testid={`request-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-base text-white">{r.asset}</span>
                        {r.has_image && (
                          <button
                            onClick={() => openImage(r.id)}
                            data-testid={`view-image-${r.id}`}
                            className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#00E5FF] hover:underline inline-flex items-center gap-1"
                          >
                            <Maximize2 className="w-3 h-3" /> view chart
                          </button>
                        )}
                      </div>
                      {r.question && (
                        <div className="text-xs text-neutral-400 mt-1">{r.question}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: st.color }}>
                      <Icon className="w-3 h-3" />{st.label}
                    </div>
                  </div>
                  {r.response && (
                    <div className="mt-3 border-t border-neutral-900 pt-3 text-sm text-neutral-200 whitespace-pre-line leading-relaxed">
                      {r.response}
                    </div>
                  )}
                  {r.response && (r.response_images || []).length > 0 && (
                    <div className="mt-3" data-testid={`response-images-${r.id}`}>
                      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-2">
                        attached screenshots ({(r.response_images || []).length})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(r.response_images || []).map((img) => (
                          <button
                            key={img.id}
                            onClick={() => openResponseImage(r.id, img)}
                            data-testid={`view-response-image-${img.id}`}
                            className="group relative w-28 h-20 border border-neutral-800 hover:border-[#00E5FF] bg-black overflow-hidden transition-colors"
                            title={img.filename || "View screenshot"}
                          >
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-neutral-500 group-hover:text-[#00E5FF]">
                              <Maximize2 className="w-4 h-4" />
                              <span className="text-[9px] font-mono uppercase tracking-[0.15em]">view</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {r.status === "completed" && (
                    <div className="mt-3 pt-3 border-t border-neutral-900">
                      <button
                        onClick={() => setTicketReview(r)}
                        data-testid={`reply-review-${r.id}`}
                        className="inline-flex items-center gap-1.5 border border-neutral-800 hover:border-[#00E5FF] hover:text-[#00E5FF] text-neutral-300 font-mono text-xs uppercase tracking-[0.2em] px-3 py-1.5 transition-colors"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        {r.has_open_ticket ? "view discussion" : "reply to review"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Ticket modal */}
      <TicketModal
        review={ticketReview}
        open={!!ticketReview}
        onClose={() => setTicketReview(null)}
        onTicketChanged={() => loadRequests()}
      />

      {/* Enlarged image lightbox */}
      {enlargedImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setEnlargedImg(null)}
          data-testid="image-lightbox"
        >
          <div className="absolute top-4 right-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <a
              href={enlargedImg.src}
              download={enlargedImg.filename || "chartlenz-screenshot.png"}
              data-testid="lightbox-download"
              className="inline-flex items-center gap-1.5 bg-white text-black font-mono text-xs uppercase tracking-[0.2em] px-3 py-2 hover:bg-neutral-200"
            >
              <Download className="w-3.5 h-3.5" /> download
            </a>
            <button onClick={() => setEnlargedImg(null)} className="text-white p-2" aria-label="close">
              <X className="w-6 h-6" />
            </button>
          </div>
          <img src={enlargedImg.src} alt={enlargedImg.filename || "chart full-size"} className="max-w-full max-h-full object-contain border border-neutral-800" />
        </div>
      )}
    </motion.section>
  );
}

function Section({ label, children }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-2">{label}</div>
      {children}
    </div>
  );
}
